import { useCallback, useEffect, useState } from "react";
import { addDays, addMonths } from "date-fns";
import api from "@/lib/api";

/** Prioritet på huskelista. Rekkjefølgje = visningsrekkjefølgje i val. */
export const PRIORITY = {
  hoeg: { label: "Høg", short: "Høg", className: "bg-primary/15 text-primary border-primary/30" },
  normal: { label: "Normal", short: "Normal", className: "bg-muted text-muted-foreground border-border" },
  laag: { label: "Låg", short: "Låg", className: "bg-muted/60 text-muted-foreground border-transparent" },
};
export const PRIORITY_OPTIONS = ["hoeg", "normal", "laag"].map((value) => ({ value, label: PRIORITY[value].label }));

/** Hurtigval for ønska dato på eit punkt. */
export const TASK_DATE_PRESETS = [
  { label: "I dag", fn: (t) => t, testid: "today" },
  { label: "I morgon", fn: (t) => addDays(t, 1), testid: "tomorrow" },
  { label: "+1 veke", fn: (t) => addDays(t, 7), testid: "plus-1w" },
  { label: "+1 md", fn: (t) => addMonths(t, 1), testid: "plus-1m" },
];

/** Kort, menneskeleg tekst for ønska dato. */
export const dueLabel = (task) => {
  if (!task?.due_date) return null;
  const d = task.days_remaining;
  if (d == null) return null;
  if (task.status === "done") return null;
  if (d < 0) return d === -1 ? "1 dag over tida" : `${Math.abs(d)} dagar over tida`;
  if (d === 0) return "I dag";
  if (d === 1) return "I morgon";
  if (d <= 7) return `Om ${d} dagar`;
  return null;
};

/** Gruppe for opne punkt i lista. */
export const dueGroup = (task) => {
  if (task.status === "done") return "done";
  if (!task.due_date) return "nodate";
  const d = task.days_remaining;
  if (d < 0) return "overdue";
  if (d === 0) return "today";
  if (d <= 7) return "week";
  return "later";
};
export const GROUP_LABEL = {
  overdue: "Over tida",
  today: "I dag",
  week: "Denne veka",
  later: "Seinare",
  nodate: "Utan dato",
  done: "Fullførte",
};
export const GROUP_ORDER = ["overdue", "today", "week", "later", "nodate", "done"];

/* ---------- Endringsvarsel: alle visningar (sidebar-teljar, dashbord, maskinside) held seg i sync ---------- */
export const TASKS_CHANGED = "vms:tasks-changed";
export const notifyTasksChanged = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(TASKS_CHANGED));
};

/** Lettvekts teljar for nav/dashbord. Oppdaterer seg ved endringar og når vindauget får fokus. */
export function useTaskSummary() {
  const [summary, setSummary] = useState(null);
  const refresh = useCallback(() => {
    api.get("/tasks/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    window.addEventListener(TASKS_CHANGED, refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener(TASKS_CHANGED, refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);
  return summary;
}

/* ---------- API-hjelparar ---------- */
export const fetchTasks = (params = {}) => api.get("/tasks", { params }).then((r) => r.data);
export const createTask = (payload) => api.post("/tasks", payload).then((r) => r.data);
export const updateTask = (id, payload) => api.patch(`/tasks/${id}`, payload).then((r) => r.data);
export const completeTask = (id) => api.post(`/tasks/${id}/complete`).then((r) => r.data);
export const reopenTask = (id) => api.post(`/tasks/${id}/reopen`).then((r) => r.data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`).then((r) => r.data);
